import React, { useEffect, useState, useCallback } from 'react';
import { reportsAPI, staffAPI, api } from '../services/api';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import InfoIconButton from '../components/InfoIconButton';
import './TravelVoucherHelperReport.css';

function formatDateDisplay(str) {
  if (!str) return '—';
  const s = String(str).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
  return str;
}

/** First and last day of previous month as YYYY-MM-DD */
function getPreviousMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  return {
    from: first.toISOString().slice(0, 10),
    to: last.toISOString().slice(0, 10),
  };
}

function csvEscape(val) {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** One-way distance × 2 when round trip is checked; null if no one-way miles. */
function totalMileage(oneWayMiles, roundTrip) {
  if (oneWayMiles == null || oneWayMiles === '') return null;
  const n = Number(oneWayMiles);
  if (Number.isNaN(n)) return null;
  return n * (roundTrip ? 2 : 1);
}

function formatTotalMileageCell(oneWayMiles, roundTrip) {
  const t = totalMileage(oneWayMiles, roundTrip);
  return t != null ? t : '—';
}

/** Google Maps directions URL with origin/destination (opens in new tab; user looks up distance and enters it). */
function getDirectionsUrl(originAddress, destinationAddress) {
  const origin = (originAddress ?? '').trim();
  const dest = (destinationAddress ?? '').trim();
  if (!origin || !dest) return null;
  const params = new URLSearchParams({
    api: '1',
    origin,
    destination: dest,
  });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export default function TravelVoucherHelperReport({ embedded = false, refreshKey = 0, tenantCode = null }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [staffId, setStaffId] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [defaultDatesSet, setDefaultDatesSet] = useState(false);
  const [roleCode, setRoleCode] = useState(null);
  const [myStaffId, setMyStaffId] = useState(null);
  const [staffDisplayName, setStaffDisplayName] = useState(null);
  const [tenantCodeFromWhoami, setTenantCodeFromWhoami] = useState(null);
  const [showIntroHint, setShowIntroHint] = useState(false);
  const [showTableHelp, setShowTableHelp] = useState(false);

  const fetchWhoami = useCallback(async () => {
    try {
      const res = await api.get('/whoami');
      const code = res.data?.membership?.role_code || null;
      const sid = res.data?.user?.staff_id ?? null;
      setRoleCode(code);
      setMyStaffId(sid != null ? String(sid) : null);
      setStaffDisplayName(res.data?.staff_display_name ?? null);
      setTenantCodeFromWhoami(res.data?.tenant?.code ?? null);
    } catch {
      setRoleCode(null);
      setMyStaffId(null);
      setStaffDisplayName(null);
      setTenantCodeFromWhoami(null);
    }
  }, []);

  useEffect(() => {
    fetchWhoami();
  }, [fetchWhoami]);

  useEffect(() => {
    const handler = () => fetchWhoami();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchWhoami]);

  useEffect(() => {
    if (defaultDatesSet) return;
    const { from, to } = getPreviousMonthRange();
    setDateFrom(from);
    setDateTo(to);
    setDefaultDatesSet(true);
  }, [defaultDatesSet]);

  useEffect(() => {
    if (roleCode === 'SERVICE_COORDINATOR') return;
    let mounted = true;
    staffAPI
      .getAll({ active: 'true' })
      .then((res) => {
        if (!mounted) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        setStaffOptions(list);
      })
      .catch(() => {
        if (!mounted) return;
        setStaffOptions([]);
      });
    return () => { mounted = false; };
  }, [tenantCode, roleCode]);

  const runReport = () => {
    const from = (dateFrom || '').trim();
    const to = (dateTo || '').trim();
    if (!from || !to) {
      setError('Please enter both date range fields (From and To).');
      return;
    }
    if (from > to) {
      setError('From date must be on or before To date.');
      return;
    }
    setError(null);
    setLoading(true);
    setShowTableHelp(false);
    const params = { date_from: from, date_to: to };
    if (roleCode === 'SERVICE_COORDINATOR' && myStaffId) {
      params.staff_id = myStaffId;
    } else if (staffId) {
      params.staff_id = staffId;
    }
    reportsAPI
      .get('travel-voucher-export', params)
      .then((res) => {
        const rawRows = res.data?.rows || [];
        setRows(rawRows.map((r) => ({
          ...r,
          includeInVoucher: false,
          roundTrip: false,
        })));
        setMeta({
          startingAddress: res.data?.startingAddress ?? '',
          program: res.data?.program ?? '',
        });
      })
      .catch((err) => {
        setRows([]);
        setMeta(null);
        setShowTableHelp(false);
        setError(err?.response?.data?.error || err?.message || 'Failed to load travel voucher data');
      })
      .finally(() => setLoading(false));
  };

  const setRowFlag = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const setRowMiles = (idx, value) => {
    const num = value === '' || value == null ? null : (parseFloat(value) || null);
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, miles: num } : r)));
  };

  const openDirections = (idx) => {
    const start = (meta?.startingAddress ?? '').trim();
    const end = (rows[idx]?.endingAddress ?? '').trim();
    const url = getDirectionsUrl(start, end);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const downloadCsv = () => {
    const included = rows.filter((r) => r.includeInVoucher);
    /** After purpose: Round trip (Yes/No), Total mileage only — one-way miles stay on screen only. */
    const columns = [
      { key: 'program', header: 'Program' },
      { key: 'travelDate', header: 'Travel date' },
      { key: 'clientName', header: 'Client' },
      { key: 'startingAddress', header: 'Starting address' },
      { key: 'endingAddress', header: 'Ending address' },
      { key: 'purpose', header: 'Purpose' },
      { key: 'roundTrip', header: 'Round trip' },
      { key: 'totalMileage', header: 'Total mileage' },
      { key: 'staffName', header: 'Staff' },
      { key: 'staffId', header: 'Staff ID' },
    ];
    const lines = [
      columns.map((c) => csvEscape(c.header)).join(','),
      ...included.map((row) => {
        const travelDateFormatted = row.travelDate ? formatDateDisplay(row.travelDate) : '';
        const roundTripLabel = row.roundTrip ? 'Yes' : 'No';
        const total = totalMileage(row.miles, !!row.roundTrip);
        const totalStr = total != null ? String(total) : '';
        return columns
          .map((c) => {
            if (c.key === 'travelDate') return csvEscape(travelDateFormatted);
            if (c.key === 'roundTrip') return csvEscape(roundTripLabel);
            if (c.key === 'totalMileage') return csvEscape(totalStr);
            return csvEscape(row[c.key] ?? '');
          })
          .join(',');
      }),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `travel-voucher-export-${dateFrom}-${dateTo}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    if (included.length === 0) {
      setError('No rows included. Check "Include" for each row you want in the voucher, then download again.');
    } else {
      setError(null);
    }
  };

  useEffect(() => {
    if (refreshKey > 0 && (dateFrom || dateTo)) runReport();
  }, [refreshKey]);

  const hasResults = rows.length > 0;
  const startingAddress = meta?.startingAddress ?? '';

  const titleSuffix = tenantCode || tenantCodeFromWhoami ? ` — ${tenantCode || tenantCodeFromWhoami}` : '';
  const isSc = roleCode === 'SERVICE_COORDINATOR';
  const scDisplayName =
    (rows.length > 0 && rows[0].staffName) ? rows[0].staffName
      : staffDisplayName || (myStaffId ? `ID ${myStaffId}` : null);

  return (
    <div className="travel-voucher-helper-report">
      <h2 className="travel-voucher-report-title travel-voucher-report-title--with-info">
        <span className="travel-voucher-report-title-text">
          Travel Voucher Helper Tool{titleSuffix}
        </span>
        <InfoIconButton
          className="travel-voucher-title-info"
          onClick={() => setShowIntroHint((v) => !v)}
          ariaExpanded={showIntroHint}
          ariaControls="tv-travel-voucher-intro-hint"
          ariaLabel="How to run this tool"
          ariaLabelExpanded="Hide how to run this tool"
        />
      </h2>
      {showIntroHint && (
        <p id="tv-travel-voucher-intro-hint" className="travel-voucher-report-hint">
          Choose a date range and click <strong>Run</strong> to load completed tracking items for the period.
        </p>
      )}
      {isSc && scDisplayName && (
        <p className="travel-voucher-report-context" aria-live="polite">
          Staff / Service Coordinator: {scDisplayName}
        </p>
      )}
      <div className="travel-voucher-filters">
        <div className="travel-voucher-filter-row">
          <label htmlFor="tv-date-from">Date from</label>
          <input
            id="tv-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-required="true"
          />
          <label htmlFor="tv-date-to">Date to</label>
          <input
            id="tv-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            aria-required="true"
          />
        </div>
        {!isSc && (
          <div className="travel-voucher-filter-row">
            <label htmlFor="tv-staff">Staff (optional)</label>
            <select
              id="tv-staff"
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
            >
              <option value="">All staff</option>
              {staffOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {[s.last_name, s.first_name].filter(Boolean).join(', ')}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="travel-voucher-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={runReport}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Run'}
          </button>
          <button
            type="button"
            className="btn btn-primary travel-voucher-download-btn"
            onClick={downloadCsv}
            disabled={rows.length === 0}
          >
            Download CSV
          </button>
        </div>
      </div>

      {startingAddress && (
        <div className="travel-voucher-hints">
          <p className="travel-voucher-hint">
            <strong>Starting address (tenant):</strong> {startingAddress}
          </p>
        </div>
      )}

      {error && (
        <div className="travel-voucher-error" role="alert">
          {error}
        </div>
      )}

      {hasResults && (
        <div className="travel-voucher-results">
          <p className="travel-voucher-count">
            <span>
              {rows.length} row{rows.length !== 1 ? 's' : ''}
            </span>
            <InfoIconButton
              className="travel-voucher-count-info"
              onClick={() => setShowTableHelp((v) => !v)}
              ariaExpanded={showTableHelp}
              ariaControls="tv-travel-voucher-table-help"
              ariaLabel="Miles, export, and CSV details"
              ariaLabelExpanded="Hide miles and export details"
            />
          </p>
          {showTableHelp && (
            <p id="tv-travel-voucher-table-help" className="travel-voucher-table-help">
              Enter <strong>one-way miles</strong> (use <strong>Get distance</strong> with start and end addresses after you check <strong>Include</strong>).
              <strong> Total mileage</strong> doubles when <strong>Round trip</strong> is checked.
              Check <strong>Include</strong> (last column) for rows to export.
              The CSV includes <strong>Round trip</strong> (Yes/No) and <strong>Total mileage</strong> only — not one-way miles.
            </p>
          )}
          <div className="travel-voucher-table-wrap">
            <table className="travel-voucher-table">
              <thead>
                <tr>
                  <th>Program</th>
                  <th>Travel date</th>
                  <th>Client</th>
                  <th>Starting address</th>
                  <th>Ending address</th>
                  <th>Purpose</th>
                  <th className="th-round-trip">Round trip</th>
                  <th>One-way miles</th>
                  <th>Total mileage</th>
                  <th>Staff</th>
                  <th className="th-include">Include</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.program}</td>
                    <td>{formatDateDisplay(row.travelDate)}</td>
                    <td>{row.clientName}</td>
                    <td>{row.startingAddress}</td>
                    <td>{row.endingAddress}</td>
                    <td>{row.purpose}</td>
                    <td className="td-checkbox">
                      <input
                        type="checkbox"
                        checked={!!row.roundTrip}
                        onChange={(e) => setRowFlag(idx, 'roundTrip', e.target.checked)}
                        aria-label={`Round trip for row ${idx + 1}`}
                      />
                    </td>
                    <td className="td-miles">
                      <input
                        type="number"
                        className="travel-voucher-miles-input"
                        min="0"
                        step="0.1"
                        value={row.miles != null ? row.miles : ''}
                        onChange={(e) => setRowMiles(idx, e.target.value)}
                        placeholder="—"
                        aria-label={`One-way miles for row ${idx + 1}`}
                      />
                      {row.includeInVoucher && meta?.startingAddress && (row.endingAddress ?? '').trim() && (
                        <button
                          type="button"
                          className="travel-voucher-get-distance-btn"
                          onClick={() => openDirections(idx)}
                        >
                          Get distance
                        </button>
                      )}
                    </td>
                    <td>{formatTotalMileageCell(row.miles, !!row.roundTrip)}</td>
                    <td>{row.staffName}</td>
                    <td className="td-checkbox">
                      <input
                        type="checkbox"
                        checked={!!row.includeInVoucher}
                        onChange={(e) => setRowFlag(idx, 'includeInVoucher', e.target.checked)}
                        aria-label={`Include row ${idx + 1} in voucher`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
